import { useState } from 'react';
import {
  DeleteButton,
  Empty,
  ErrorBanner,
  Field,
  Loading,
  Modal,
  PageHead,
  ScaleInput,
} from '../components/ui';
import { api } from '../lib/api';
import { formatDate, todayLocal, verdictBadge } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { AccountabilityChart, GwcRow, GwcVerdict, Me, Seat } from '../lib/types';

const LETTERS = [
  { key: 'G', label: 'Get it', blurb: 'They genuinely understand the role — the whys, the nuances, the pace.' },
  { key: 'W', label: 'Want it', blurb: 'They want the seat, without being sold on it or paid extra to take it.' },
  { key: 'C', label: 'Capacity', blurb: 'They have the time, mental and emotional capacity, and skill to do it well.' },
] as const;

/**
 * Leadership GWC board.
 *
 * GWC is judged per SEAT, not per person: the same leader can be a clear yes
 * in one seat and a no in another. Rows with no assessment yet are shown
 * deliberately — an unassessed leader is the finding.
 */
export function GwcPage() {
  const [scope, setScope] = useState<'leadership' | 'all'>('leadership');
  const rows = useResource<GwcRow[]>(`/gwc${scope === 'all' ? '?scope=all' : ''}`);
  const chart = useResource<AccountabilityChart>('/chart/current');
  const [editing, setEditing] = useState<GwcRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* Read here rather than threaded down from App, the same way the OML and
     Account Management pages do it: /me is already in the browser's cache by
     the time any page renders. */
  const me = useResource<Me>('/me');
  const isAdmin = me.data?.appRole === 'admin';

  const data = rows.data ?? [];
  const assessed = data.filter((r) => r.Verdict !== null);
  const clearYes = assessed.filter((r) => r.Verdict === 'yes').length;
  const concerns = assessed.filter((r) => r.Verdict === 'no').length;

  /* One person can hold several seats and is assessed once per seat, so rows
     outnumber people. Count distinct people or the totals read as headcount
     and overstate the team. */
  const people = new Set(data.map((r) => r.EmployeeId)).size;
  const assessedPeople = new Set(assessed.map((r) => r.EmployeeId)).size;
  const unassessed = people - assessedPeople;

  return (
    <>
      <PageHead
        title="GWC Assessment"
        lede="Get it, Want it, Capacity to do it. All three must be yes — a no in any one of them is a no overall, and that is the conversation to have."
        actions={
          <div className="gwc-toggle">
            <button className={scope === 'leadership' ? 'on-yes' : ''} onClick={() => setScope('leadership')}>
              Leadership
            </button>
            <button className={scope === 'all' ? 'on-yes' : ''} onClick={() => setScope('all')}>
              Everyone
            </button>
          </div>
        }
      />

      {rows.error && <ErrorBanner message={rows.error} />}
      {error && <ErrorBanner message={error} />}
      {rows.loading && <Loading />}

      {!rows.loading && (
        <div className="grid grid-4 mb-4">
          <div className="stat">
            <div className="label">Assessed</div>
            <div className="figure">{assessedPeople}</div>
            <div className="foot">of {people} {people === 1 ? 'person' : 'people'}</div>
          </div>
          <div className="stat">
            <div className="label">Clear yes</div>
            <div className="figure">{clearYes}</div>
            <div className="foot">{clearYes === 1 ? 'seat' : 'seats'}, all three letters</div>
          </div>
          <div className="stat">
            <div className="label">Concerns</div>
            <div className={`figure${concerns > 0 ? ' accent' : ''}`}>{concerns}</div>
            <div className="foot">{concerns === 1 ? 'seat' : 'seats'} with a no</div>
          </div>
          <div className="stat">
            <div className="label">Not yet assessed</div>
            <div className={`figure${unassessed > 0 ? ' accent' : ''}`}>{unassessed}</div>
            <div className="foot">no record on file</div>
          </div>
        </div>
      )}

      {!rows.loading && data.length === 0 && (
        <Empty title="Nobody to assess yet">
          <p>
            Add people on the Employees page, and mark their role as leadership to have them appear here by
            default.
          </p>
        </Empty>
      )}

      {data.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Seat / Role</th>
                  <th className="center">Get it</th>
                  <th className="center">Want it</th>
                  <th className="center">Capacity</th>
                  <th className="center">Verdict</th>
                  <th>Assessed</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.map((r) => (
                  <tr key={`${r.EmployeeId}-${r.GwcId ?? 'none'}`}>
                    <td>
                      <div className="strong">{r.FullName}</div>
                      {r.DepartmentName && <div className="xs muted">{r.DepartmentName}</div>}
                    </td>
                    <td>
                      {r.SeatName ?? r.RoleTitle ?? <span className="subtle">—</span>}
                    </td>
                    <LetterCell value={r.GetIt} score={r.GetItScore} />
                    <LetterCell value={r.WantIt} score={r.WantItScore} />
                    <LetterCell value={r.Capacity} score={r.CapacityScore} />
                    <td className="center">
                      {r.Verdict ? (
                        <span className={`badge ${verdictBadge(r.Verdict)}`}>
                          {r.Verdict === 'yes' ? 'GWC' : r.Verdict === 'no' ? 'Not GWC' : 'Unsure'}
                        </span>
                      ) : (
                        <span className="badge badge-neutral">Not assessed</span>
                      )}
                    </td>
                    <td className="mono xs">{formatDate(r.AssessedOn)}</td>
                    <td className="actions">
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(r)}>
                        {r.GwcId ? 'Update' : 'Assess'}
                      </button>
                      {/* Admin only, and only where there is something to
                          delete: a row with no GwcId is a seat nobody has
                          assessed yet, which is a finding rather than a record.

                          Deleting is not the same as marking somebody "not
                          GWC" — that verdict is the point of the board and is
                          set by assessing. This is for an assessment that
                          should never have been filed: the wrong seat, a
                          duplicate, a test. Judging a named person in a named
                          seat is not something to let any editor erase, which
                          is why it sits behind admin on both sides. */}
                      {isAdmin && r.GwcId && (
                        <DeleteButton
                          what={`the GWC assessment of ${r.FullName} in ${r.SeatName ?? r.RoleTitle ?? 'this seat'}`}
                          onConfirm={() => {
                            setError(null);
                            void api
                              .del(`/gwc/${r.GwcId}`)
                              .then(rows.reload)
                              .catch((err: unknown) =>
                                setError(
                                  err instanceof Error ? err.message : 'Could not delete that assessment',
                                ),
                              );
                          }}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <GwcForm
          row={editing}
          seats={flattenSeats(chart.data?.seats ?? [])}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            rows.reload();
          }}
        />
      )}
    </>
  );
}

function LetterCell({ value, score }: { value: GwcVerdict | null; score: number | null }) {
  if (!value) {
    return (
      <td className="center">
        <span className="subtle">—</span>
      </td>
    );
  }
  return (
    <td className="center">
      <span className={`badge ${verdictBadge(value)}`}>
        {value === 'yes' ? 'Yes' : value === 'no' ? 'No' : 'Unsure'}
      </span>
      {score !== null && <div className="xs muted mono mt-2">{score}/5</div>}
    </td>
  );
}

function flattenSeats(seats: Seat[]): { SeatId: number; Name: string }[] {
  const out: { SeatId: number; Name: string }[] = [];
  const walk = (list: Seat[], depth: number) => {
    for (const s of list) {
      out.push({ SeatId: s.SeatId, Name: `${'— '.repeat(depth)}${s.Name}` });
      walk(s.children ?? [], depth + 1);
    }
  };
  walk(seats, 0);
  return out;
}

function GwcForm({
  row,
  seats,
  onClose,
  onSaved,
}: {
  row: GwcRow;
  seats: { SeatId: number; Name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    seatId: (row.SeatId ?? '') as number | '',
    assessedOn: row.AssessedOn?.slice(0, 10) ?? todayLocal(),
    getIt: (row.GetIt ?? 'unsure') as GwcVerdict,
    getItScore: row.GetItScore,
    getItNotes: row.GetItNotes ?? '',
    wantIt: (row.WantIt ?? 'unsure') as GwcVerdict,
    wantItScore: row.WantItScore,
    wantItNotes: row.WantItNotes ?? '',
    capacity: (row.Capacity ?? 'unsure') as GwcVerdict,
    capacityScore: row.CapacityScore,
    capacityNotes: row.CapacityNotes ?? '',
    actionPlan: row.ActionPlan ?? '',
    nextReviewDate: row.NextReviewDate?.slice(0, 10) ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  /* Mirrors the computed column in SQL so the preview matches what gets
     stored — a no anywhere is a no overall. */
  const verdict: GwcVerdict =
    form.getIt === 'no' || form.wantIt === 'no' || form.capacity === 'no'
      ? 'no'
      : form.getIt === 'unsure' || form.wantIt === 'unsure' || form.capacity === 'unsure'
        ? 'unsure'
        : 'yes';

  const submit = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      employeeId: row.EmployeeId,
      seatId: form.seatId === '' ? null : Number(form.seatId),
      assessedOn: form.assessedOn || null,
      getIt: form.getIt,
      getItScore: form.getItScore,
      getItNotes: form.getItNotes || null,
      wantIt: form.wantIt,
      wantItScore: form.wantItScore,
      wantItNotes: form.wantItNotes || null,
      capacity: form.capacity,
      capacityScore: form.capacityScore,
      capacityNotes: form.capacityNotes || null,
      actionPlan: form.actionPlan || null,
      nextReviewDate: form.nextReviewDate || null,
    };
    try {
      /* Updating an existing record keeps one row per review point; a new
         assessment date should be a NEW row so the history survives. Editing
         is therefore only offered for the record already on screen. */
      if (row.GwcId) await api.put(`/gwc/${row.GwcId}`, payload);
      else await api.post('/gwc', payload);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  const letterState = {
    G: { value: form.getIt, score: form.getItScore, notes: form.getItNotes },
    W: { value: form.wantIt, score: form.wantItScore, notes: form.wantItNotes },
    C: { value: form.capacity, score: form.capacityScore, notes: form.capacityNotes },
  } as const;

  const setLetter = (key: 'G' | 'W' | 'C', patch: { value?: GwcVerdict; score?: number | null; notes?: string }) => {
    if (key === 'G')
      set({
        ...(patch.value !== undefined && { getIt: patch.value }),
        ...(patch.score !== undefined && { getItScore: patch.score }),
        ...(patch.notes !== undefined && { getItNotes: patch.notes }),
      });
    if (key === 'W')
      set({
        ...(patch.value !== undefined && { wantIt: patch.value }),
        ...(patch.score !== undefined && { wantItScore: patch.score }),
        ...(patch.notes !== undefined && { wantItNotes: patch.notes }),
      });
    if (key === 'C')
      set({
        ...(patch.value !== undefined && { capacity: patch.value }),
        ...(patch.score !== undefined && { capacityScore: patch.score }),
        ...(patch.notes !== undefined && { capacityNotes: patch.notes }),
      });
  };

  return (
    <Modal
      title={`GWC — ${row.FullName}`}
      onClose={onClose}
      footer={
        <>
          <span className={`badge ${verdictBadge(verdict)}`} style={{ marginRight: 'auto' }}>
            {verdict === 'yes' ? 'Clear GWC' : verdict === 'no' ? 'Not a fit for this seat' : 'Unresolved'}
          </span>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void submit()}>
            {saving ? 'Saving…' : 'Save assessment'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <div className="form-grid">
        <Field label="Seat" hint="GWC is judged against a specific seat.">
          <select
            className="select"
            value={form.seatId}
            onChange={(e) => set({ seatId: e.target.value === '' ? '' : Number(e.target.value) })}
          >
            <option value="">No seat selected</option>
            {seats.map((s) => (
              <option key={s.SeatId} value={s.SeatId}>
                {s.Name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Assessed on">
          <input className="input" type="date" value={form.assessedOn} onChange={(e) => set({ assessedOn: e.target.value })} />
        </Field>
      </div>

      {LETTERS.map((letter) => {
        const state = letterState[letter.key];
        return (
          <div
            key={letter.key}
            style={{
              borderTop: '1px solid var(--border-subtle)',
              paddingTop: 'var(--space-4)',
            }}
          >
            <div className="row-between wrap">
              <div className="grow" style={{ minWidth: 200 }}>
                <div className="strong">
                  <span className="mono" style={{ color: 'var(--text-accent)' }}>
                    {letter.key}
                  </span>{' '}
                  {letter.label}
                </div>
                <p className="xs muted">{letter.blurb}</p>
              </div>
              <div className="gwc-toggle">
                {(['yes', 'unsure', 'no'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={state.value === v ? `on-${v}` : ''}
                    onClick={() => setLetter(letter.key, { value: v })}
                  >
                    {v === 'yes' ? 'Yes' : v === 'unsure' ? 'Unsure' : 'No'}
                  </button>
                ))}
              </div>
            </div>

            <div className="row wrap mt-2">
              <span className="xs muted">Confidence</span>
              <ScaleInput value={state.score} onChange={(score) => setLetter(letter.key, { score })} />
            </div>

            <textarea
              className="textarea mt-2"
              rows={2}
              placeholder="Evidence — a specific example beats an adjective."
              value={state.notes}
              onChange={(e) => setLetter(letter.key, { notes: e.target.value })}
            />
          </div>
        );
      })}

      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}>
        <Field label="Action plan">
          <textarea
            className="textarea"
            rows={3}
            placeholder="What happens next — coaching, a change of seat, or a date to revisit."
            value={form.actionPlan}
            onChange={(e) => set({ actionPlan: e.target.value })}
          />
        </Field>
        <div className="mt-4">
          <Field label="Next review">
            <input
              className="input"
              type="date"
              value={form.nextReviewDate}
              onChange={(e) => set({ nextReviewDate: e.target.value })}
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
